import { useState } from 'react';
import {
  Text,
  TextInput,
  Textarea,
  Stack,
  Divider,
  FileInput,
  MultiSelect,
  Group,
  Button,
} from '@mantine/core';
import { IconUpload, IconCamera } from '@tabler/icons-react';
import { ResourceInput } from '@medplum/react';
import {
  CodeableConcept,
  DocumentReference,
  Patient,
  Practitioner,
} from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { ANSWER_DELIVERY_OPTIONS } from '../constants/deliveryOptions';
import { ARKON } from '@/consts/systems.consts';
import { CodeableConceptInput } from '@/components/medplum-react/CodeableConceptInput/CodeableConceptInput';
import { DateInputWrapper } from '@/components/inputs/DateInputWrapper';
import { DocumentScannerModal } from '@/components/DocumentScanner/DocumentScannerModal';
import { DocumentReferenceDisplay } from '@/components/DocumentReferenceDisplay';
import { hasAuthorizedPerson } from '../utils/common-helpers';

const acceptedFileTypes =
  'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*';
export interface OrderFormData {
  patient: Patient | undefined;
  requester: Practitioner | undefined;
  testCode: string;
  testDisplay: string;
  sampleType: CodeableConcept | undefined;
  clinicalNotes: string;
  answerDelivery: string[];
  authorizedPerson: string;
  authorizationFormFile: File | undefined;
  orderDate: Date;
  orderFile: File | undefined;
  referralReason: string;
  symptomOnsetAge: string;
  firstSymptom: string;
}

export interface OrderFormErrors {
  patient?: string;
  requester?: string;
  testCode?: string;
  sampleType?: string;
  answerDelivery?: string;
  authorizedPerson?: string;
  orderFile?: string;
  orderDate?: string;
  authorizationForm?: string;
}

interface OrderFormProps {
  formData: OrderFormData;
  errors: OrderFormErrors;
  onFieldChange: <K extends keyof OrderFormData>(
    field: K,
    value: OrderFormData[K]
  ) => void;
  readOnly?: boolean;
  linkedOrderDocuments?: DocumentReference[];
  linkedAuthDocuments?: DocumentReference[];
  documentsToDelete?: string[];
  onDocumentsToDeleteChange?: (ids: string[]) => void;
}

export function OrderForm({
  formData,
  errors,
  onFieldChange,
  readOnly = false,
  linkedOrderDocuments = [],
  linkedAuthDocuments = [],
  documentsToDelete = [],
  onDocumentsToDeleteChange,
}: OrderFormProps) {
  const { t } = useTranslation('lims');
  const [orderFileScannerOpened, setOrderFileScannerOpened] = useState(false);
  const [authFormScannerOpened, setAuthFormScannerOpened] = useState(false);

  const existingOrderDocs = linkedOrderDocuments.filter(
    (doc) => !documentsToDelete.includes(doc.id || '')
  );
  const existingAuthDocs = linkedAuthDocuments.filter(
    (doc) => !documentsToDelete.includes(doc.id || '')
  );

  const handleDeleteOrderDocument = (documentId: string) => {
    onDocumentsToDeleteChange?.([...documentsToDelete, documentId]);
  };
  const handleDeleteAuthDocument = (documentId: string) => {
    onDocumentsToDeleteChange?.([...documentsToDelete, documentId]);
  };

  const answerDeliveryData = ANSWER_DELIVERY_OPTIONS.map((o) => ({
    value: o.value,
    label: o.label,
  }));

  return (
    <Stack gap="md">
      <ResourceInput<Patient>
        resourceType="Patient"
        name="patient"
        label={t('orderForm.labels.patient')}
        key={`patient-${formData.patient?.id || 'new'}`}
        defaultValue={formData.patient}
        onChange={(value) => onFieldChange('patient', value)}
        required
        disabled={readOnly}
      />
      <ResourceInput<Practitioner>
        resourceType="Practitioner"
        name="requester"
        label={t('orderForm.labels.requester')}
        key={`requester-${formData.requester?.id || 'new'}`}
        defaultValue={formData.requester}
        onChange={(value) => onFieldChange('requester', value)}
        required
        disabled={readOnly}
      />
      <CodeableConceptInput
        path="ServiceRequest.code"
        binding={ARKON.VS.GENETIC_TESTS_EL}
        label={t('orderForm.labels.geneticTest')}
        name="geneticTest"
        maxValues={1}
        defaultValue={
          formData.testCode
            ? {
                coding: [{ code: formData.testCode }],
              }
            : undefined
        }
        onChange={(value) => {
          const coding = value?.coding?.[0];
          onFieldChange('testCode', coding?.code || '');
          onFieldChange('testDisplay', coding?.display || '');
        }}
        required
        disabled={readOnly}
        placeholder="πχ. Καρυότυπος περιφερικού αίματος"
        loadAll
      />
      <CodeableConceptInput
        path="ServiceRequest.code"
        binding={ARKON.VS.SPECIMEN_TYPES_EL}
        label={t('orderForm.labels.sampleType')}
        name="sampleType"
        maxValues={1}
        defaultValue={formData.sampleType}
        onChange={(c) => {
          onFieldChange('sampleType', c);
        }}
        required
        disabled={readOnly}
        placeholder={t('specimenModal.sampleTypePlaceholder')}
        loadAll
      />
      <DateInputWrapper
        label={t('orderForm.labels.orderDate')}
        value={formData.orderDate}
        onChange={(date) => onFieldChange('orderDate', date ?? new Date())}
        disabled={readOnly}
      />
      <MultiSelect
        label={t('orderForm.labels.answerDelivery')}
        placeholder={t('orderForm.placeholders.selectDeliveryMethod')}
        value={formData.answerDelivery}
        onChange={(values) => {
          onFieldChange('answerDelivery', values);
          if (!hasAuthorizedPerson(values)) {
            onFieldChange('authorizedPerson', '');
            onFieldChange('authorizationFormFile', undefined);
          }
        }}
        data={answerDeliveryData}
        required
        error={errors.answerDelivery}
        disabled={readOnly}
      />
      {hasAuthorizedPerson(formData.answerDelivery) && (
        <Stack gap="sm">
          <TextInput
            label={t('orderForm.labels.authorizedPersonName')}
            placeholder={t('orderForm.placeholders.enterAuthorizedPersonName')}
            value={formData.authorizedPerson}
            onChange={(e) => onFieldChange('authorizedPerson', e.target.value)}
            required
            error={errors.authorizedPerson}
            disabled={readOnly}
          />
          <Stack gap={4}>
            <Stack gap={0}>
              <Group gap={4}>
                <Text size="sm" fw={500}>
                  {t('orderForm.labels.authorizationForm')}
                </Text>
                <Text c="red" fw={500}>
                  *
                </Text>
              </Group>
              <Text size="xs" c="dimmed">
                {t('orderForm.descriptions.authorizationForm')}
              </Text>
            </Stack>

            {existingAuthDocs.length > 0 && (
              <Stack gap="xs" mt="-4px">
                {existingAuthDocs.map((doc) => (
                  <DocumentReferenceDisplay
                    key={doc.id}
                    file={doc}
                    onDelete={handleDeleteAuthDocument}
                  />
                ))}
              </Stack>
            )}

            {existingAuthDocs.length === 0 && (
              <Group gap="sm" grow>
                <Button
                  variant="light"
                  leftSection={<IconCamera size={18} />}
                  onClick={() => setAuthFormScannerOpened(true)}
                  disabled={readOnly}
                >
                  {t('orderForm.scanDocument')}
                </Button>
                <FileInput
                  placeholder={
                    formData.authorizationFormFile?.name ??
                    t('orderForm.placeholders.uploadAuthorizationForm')
                  }
                  accept={acceptedFileTypes}
                  value={formData.authorizationFormFile ?? null}
                  onChange={(file) =>
                    onFieldChange('authorizationFormFile', file ?? undefined)
                  }
                  required
                  disabled={readOnly}
                  leftSection={<IconUpload size={18} />}
                />
              </Group>
            )}
            {errors.authorizationForm && (
              <Text size="xs" c="red">
                {errors.authorizationForm}
              </Text>
            )}
          </Stack>
        </Stack>
      )}
      <Divider
        label={t('orderForm.labels.clinicalData')}
        labelPosition="left"
      />
      <TextInput
        label={t('orderForm.labels.referralReason')}
        placeholder={t('orderForm.placeholders.enterReferralReason')}
        value={formData.referralReason}
        onChange={(e) => onFieldChange('referralReason', e.target.value)}
        disabled={readOnly}
      />
      <TextInput
        label={t('orderForm.labels.symptomOnsetAge')}
        placeholder={t('orderForm.placeholders.symptomOnsetAge')}
        value={formData.symptomOnsetAge}
        onChange={(e) => onFieldChange('symptomOnsetAge', e.target.value)}
        disabled={readOnly}
      />
      <TextInput
        label={t('orderForm.labels.firstSymptom')}
        placeholder={t('orderForm.placeholders.enterFirstSymptom')}
        value={formData.firstSymptom}
        onChange={(e) => onFieldChange('firstSymptom', e.target.value)}
        disabled={readOnly}
      />
      <Textarea
        label={t('orderForm.labels.notes')}
        placeholder={t('orderForm.placeholders.enterClinicalNotes')}
        rows={4}
        value={formData.clinicalNotes}
        onChange={(e) => onFieldChange('clinicalNotes', e.target.value)}
        disabled={readOnly}
      />
      <Stack gap={4}>
        <Stack gap={0}>
          <Text size="sm" fw={500}>
            {t('orderForm.labels.orderFile')}
          </Text>
          <Text size="xs" c="dimmed">
            {t('orderForm.descriptions.orderFile')}
          </Text>
        </Stack>

        {existingOrderDocs.length > 0 && (
          <Stack gap="xs" mt="-4px">
            {existingOrderDocs.map((doc) => (
              <DocumentReferenceDisplay
                key={doc.id}
                file={doc}
                onDelete={handleDeleteOrderDocument}
              />
            ))}
          </Stack>
        )}

        {existingOrderDocs.length === 0 && (
          <Group gap="sm" grow>
            <Button
              variant="light"
              leftSection={<IconCamera size={18} />}
              onClick={() => setOrderFileScannerOpened(true)}
              disabled={readOnly}
            >
              {t('orderForm.scanDocument')}
            </Button>
            <FileInput
              placeholder={
                formData.orderFile?.name ??
                t('orderForm.placeholders.uploadOrderFile')
              }
              accept={acceptedFileTypes}
              value={formData.orderFile ?? null}
              onChange={(file) => onFieldChange('orderFile', file ?? undefined)}
              disabled={readOnly}
              leftSection={<IconUpload size={18} />}
            />
          </Group>
        )}
      </Stack>

      <DocumentScannerModal
        opened={orderFileScannerOpened}
        onClose={() => setOrderFileScannerOpened(false)}
        onScanComplete={(scannedFile) => {
          onFieldChange('orderFile', scannedFile);
          setOrderFileScannerOpened(false);
        }}
        preferredCamera="environment"
      />

      <DocumentScannerModal
        opened={authFormScannerOpened}
        onClose={() => setAuthFormScannerOpened(false)}
        onScanComplete={(scannedFile) => {
          onFieldChange('authorizationFormFile', scannedFile);
          setAuthFormScannerOpened(false);
        }}
        preferredCamera="environment"
      />
    </Stack>
  );
}
